import { FC, useEffect } from 'react';
import { shallowEqual, useStore } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/reduxCustomHooks';
import { logoutAction } from 'src/app/slices/authSlice';
import { createSessionHooks } from 'src/app/services/baseQuery';
import { refreshIfDue } from 'src/app/services/authSession';
import { isExpiredJwt } from 'src/utils/parseJwtUtils';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;
const ACTIVITY_THROTTLE_MS = 30_000;

export const PrivateRoute: FC = () => {
  const token = useAppSelector((state) => state.auth?.token, shallowEqual);
  const dispatch = useAppDispatch();
  const store = useStore();

  // Expired is judged with a small clock-skew allowance.
  const expired = token ? isExpiredJwt(token) : false;
  const hasValidToken = token ? !expired : false;

  if (token && expired) {
    dispatch(logoutAction());
  }

  // Sliding session: user input past the half-life refreshes the token once.
  // Idle users do not refresh, so an unused session still expires.
  useEffect(() => {
    if (!hasValidToken) return;
    const hooks = createSessionHooks(store.getState, store.dispatch);
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < ACTIVITY_THROTTLE_MS) return;
      last = now;
      void refreshIfDue(hooks);
    };
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { passive: true })
    );
    return () =>
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity)
      );
  }, [hasValidToken, store]);

  return hasValidToken ? <Outlet /> : <Navigate to="/login" />;
};
